import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import {
  AlertIcon,
  TrashIcon,
  Warning,
  ZipFile,
} from '@dynamika-shared/ui/icons';
import React, { FC } from 'react';
import { UploadFileBox } from '@components/UploadFileBox/UploadFileBox';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';
import { UploadDescription } from './UploadFileStepDescriptions';
import { UploadFileTypeEnum } from '@dynamika-shared/types/inventory';
import { detectFileExtensions } from '../utils/detectFileExtension';

type CommentViewProps = {
  setAdditionalComment: (comment: string) => void;
};

type FileTypeErrorProps = {
  fileType: UploadFileTypeEnum | null;
};

export const CommentView: FC<CommentViewProps> = ({ setAdditionalComment }) => {
  return (
    <div className='mx-auto my-0 max-w-[70%]'>
      <p className='ml-2 text-1624 font-medium'>Additional Comment</p>
      <TextArea
        className='mb-3 h-[96px] !min-h-[75px] w-[642px] !resize-none'
        placeholder='Add comment here'
        classNameInputWrapper={'max-w-[100%]'}
        onChange={e => setAdditionalComment(e.target.value)}
        maxLength={500}
      />
      <p className='my-auto flex w-[642px] text-left'>
        <Warning className='mr-1 mt-[-3px] h-3 w-3 text-vividTangelo' />
        <span>
          Please make sure that all information is correct and valid until
          proceed to next steps. You can edit any info by clicking on tabs on
          top
        </span>
      </p>
    </div>
  );
};

type UploadBoxProps = {
  progress: number;
  file: File | null | undefined;
  setFile: (file: File) => void;
  acceptFile?: string;
};

export const UploadBox: FC<UploadBoxProps> = ({
  progress,
  file,
  setFile,
  acceptFile,
}) => {
  const fileType = useSiteUploadSlice(state => state.fileType);
  return (
    <div className='mx-auto max-w-[512px] w-full'>
      <UploadFileBox
        onAttach={(file: File) => {
          setFile(file);
        }}
        progress={progress}
        file={file}
        acceptFile={acceptFile}
        className={'max-h-[192px] rounded bg-white'}
        description={<UploadDescription fileType={fileType} />}
      />
    </div>
  );
};

export const FileTypeError = ({ fileType }: FileTypeErrorProps) => {
  const fileTypeInUserFormat = detectFileExtensions({ fileType });

  return (
    <div className='mb-2 w-full rounded-2xl border-redOrange border-[1px] bg-seashell px-2 py-1.5'>
      <p className='flex items-center'>
        <AlertIcon className='mr-1 h-2 w-2 text-redOrange' />{' '}
        {`Please select a
        ${fileTypeInUserFormat} format file`}
      </p>
    </div>
  );
};

type UploadedFileProps = {
  file: File | null | undefined;
  setFile: (file: File | null) => void;
};
export const UploadedFile: FC<UploadedFileProps> = ({ file, setFile }) => (
  <div className='flex w-[440px] max-w-[440px] items-center justify-between rounded-[24px] border border-dodgerBlue p-3 bg-white'>
    <div className='flex w-11/12'>
      <ZipFile className='mr-2 h-5 w-5' />
      <div className='w-full overflow-hidden'>
        <p className='overflow-hidden text-ellipsis text-1624 font-medium text-chineseBlack'>
          {file?.name}
        </p>
        <p className='text-1216 text-dimGray'>Selected for Upload</p>
      </div>
    </div>
    <TrashIcon
      className='h-3 w-3 cursor-pointer hover:text-dodgerBlue'
      onClick={() => setFile(null)}
    />
  </div>
);
